import { z } from "zod";
import { clinicalPhaseSchema } from "./clinical-phase";
import { meetingPrescriptionOrderSchema } from "./prescription-cycle";
import {
  prescriptionDecisionSchema,
  webdocControlSchema,
} from "./prescription-choice";

// CP-33 — the doctor's published monthly medical review,
// served on its own case-bound patient route (carebyella://medical-review/
// monthly/latest). Mirrors the TES-201 lab-feedback discipline: nothing is
// served until the doctor has published (`review: null` before that — the
// view is unreachable-by-design until the CP-33 card links it, but the API
// still never leaks unpublished state). The dose/prescription decision rides
// along only once the review's cycle has a decided outcome.

export const monthlyReviewDecisionSchema = z.object({
  // approved renewal → "renewed" (next cycle spawned), rejected → "ended".
  outcome: z.enum(["renewed", "ended"]),
  // The NEW cycle's dose label on renewal; null when treatment ended.
  dose: z.string().nullable(),
  decidedAt: z.string().nullable(), // ISO
});
export type MonthlyReviewDecision = z.infer<typeof monthlyReviewDecisionSchema>;

export const monthlyMedicalReviewSchema = z.object({
  publishedAt: z.string(), // ISO — the doctor's publish gate timestamp
  summary: z.string(), // the doctor's patient-facing feedback (free text)
  cycleNumber: z.number().int().positive(),
  decision: monthlyReviewDecisionSchema.nullable(),
});
export type MonthlyMedicalReview = z.infer<typeof monthlyMedicalReviewSchema>;

export const monthlyMedicalReviewResponseSchema = z.object({
  review: monthlyMedicalReviewSchema.nullable(),
});
export type MonthlyMedicalReviewResponse = z.infer<
  typeof monthlyMedicalReviewResponseSchema
>;

// Doctor input — publish (or re-publish with edited text) the patient-facing
// summary for a reviewed cycle. Publishing is write+gate in one action, like
// the labs "Godkänn och skicka"-flow.
//
// TES-1011 — `clinicalPhase` is the monthly review's phase decision, published
// with the feedback rather than saved on its own: the feedback is what
// explains the decision to her. The TES-841 contract enum and the same rule —
// explicit, audited, never defaulted — so absent means "no phase decision",
// never "keep whatever it was". The API requires it while a keep-wish is open
// (see isKeepWishOpen): WEIGHT_STABILISATION or, as `active_treatment`,
// CONTINUE_ACTIVE_TREATMENT.
//
// The summary is trimmed before the length check: a feedback of only spaces
// is no explanation, and when the doctor continues active treatment past her
// keep-wish this text is the published explanation the canon requires.
//
// TES-1034 (Emelie, points 2 and 3) — nothing is published to the patient
// before the compiled note is in Webdoc: `webdocControl.journalInWebdoc` is
// required by the API (400 journal_not_in_webdoc), and `journalOrder` is the
// order on screen the note is assembled with (the same fields as
// monthlyJournalConfirmationFields in work-queue.ts).
export const publishMonthlyReviewSchema = z.object({
  patientSummary: z.string().trim().min(1).max(4000),
  clinicalPhase: clinicalPhaseSchema.optional(),
  webdocControl: webdocControlSchema.optional(),
  journalOrder: meetingPrescriptionOrderSchema.optional(),
  // TES-1058 — the prescription status chosen in the Webdoc step, read only
  // into the note (monthlyJournalConfirmationFields says why).
  journalPrescription: prescriptionDecisionSchema.optional(),
});
export type PublishMonthlyReviewInput = z.infer<
  typeof publishMonthlyReviewSchema
>;
